import type { LayerId, Rank } from "../types";

export interface LayerDef {
  id: LayerId;
  label: string;
  /** One line under the label in the layer picker. */
  blurb: string;
  color: string;
  /**
   * Label colour on the map, per theme: a dark (light theme) or pale (dark theme)
   * tone of `color`, so a feature's name pairs with its icon and never reads as a
   * town name, which is grey.
   */
  labelColor: { light: string; dark: string };
  /** Marker glyph drawn by map/icons.ts. */
  shape: "circle" | "triangle" | "diamond" | "square" | "note" | "rail" | "horseshoe";
  file: string;
  defaultOn: boolean;
  /**
   * Zoom from which a feature of each rank gets an icon + (space permitting) a label.
   * Below that it is a dot. Rank 3 = headline, 2 = worth the detour, 1 = the rest.
   */
  tiers: Record<Rank, number>;
  attribution?: string;
}

export const LAYER_ORDER: LayerId[] = ["history", "nature", "gaa", "equine", "food", "sessions", "trains"];

export const LAYERS: Record<LayerId, LayerDef> = {
  history: {
    id: "history",
    label: "History",
    blurb: "Tombs, forts, monasteries, castles, battlefields — with parking and the walk in.",
    color: "#2f5d50",
    labelColor: { light: "#24493f", dark: "#a8dcc8" },
    shape: "circle",
    file: "sites.min.json",
    defaultOn: true,
    tiers: { 3: 0, 2: 7.5, 1: 9.5 },
  },
  nature: {
    id: "nature",
    label: "Land & sea",
    blurb: "Geology, wildlife, passes and headlands, where locals swim.",
    color: "#3a7d44",
    labelColor: { light: "#2b6134", dark: "#a3d8a6" },
    shape: "triangle",
    file: "layers/nature.min.json",
    defaultOn: true,
    tiers: { 3: 0, 2: 7.5, 1: 9.5 },
  },
  gaa: {
    id: "gaa",
    label: "GAA",
    blurb: "County grounds, legendary clubs, handball alleys, road bowling — and how to get in.",
    color: "#b8860b",
    labelColor: { light: "#7a5606", dark: "#ecc45e" },
    shape: "diamond",
    file: "layers/gaa.min.json",
    defaultOn: true,
    tiers: { 3: 0, 2: 8, 1: 9.5 },
  },
  equine: {
    id: "equine",
    label: "Horses",
    blurb: "Racecourses, point-to-points and horse trials \u2014 and what is on while you're there.",
    color: "#9c2c4e",
    labelColor: { light: "#7a1f3b", dark: "#f0a6c0" },
    shape: "horseshoe",
    file: "layers/equine.min.json",
    defaultOn: true,
    tiers: { 3: 0, 2: 8, 1: 9.5 },
  },
  food: {
    id: "food",
    label: "Eat local",
    blurb: "Chippers, chowder, blaas, boxty, the spice bag — where locals send their cousins.",
    color: "#c2410c",
    labelColor: { light: "#963208", dark: "#f4a880" },
    shape: "square",
    file: "layers/food.min.json",
    defaultOn: true,
    tiers: { 3: 8.5, 2: 8.5, 1: 9.5 },
  },
  sessions: {
    id: "sessions",
    label: "Trad sessions",
    blurb: "Pubs with a regular session, from The Session's open data.",
    color: "#6a4c93",
    labelColor: { light: "#553a78", dark: "#cbb6ea" },
    shape: "note",
    file: "layers/sessions.min.json",
    defaultOn: true,
    tiers: { 3: 9.5, 2: 9.5, 1: 9.5 },
    attribution: 'Sessions © <a href="https://thesession.org">The Session</a> (ODbL)',
  },
  trains: {
    id: "trains",
    label: "Trains",
    blurb: "Every station on the network, plus the heritage lines you can still ride.",
    color: "#1d4e89",
    labelColor: { light: "#173e6c", dark: "#9cc3f2" },
    shape: "rail",
    file: "layers/trains.min.json",
    defaultOn: true,
    tiers: { 3: 8, 2: 8, 1: 9.5 },
    attribution: 'Stations © <a href="https://www.irishrail.ie">Iarnród Éireann</a> / <a href="https://www.translink.co.uk">Translink</a>',
  },
};

// ---- History facets (unchanged from v1) ----
export const PERIODS: [string, string][] = [
  ["neolithic", "Neolithic"],
  ["bronze_age", "Bronze Age"],
  ["iron_age", "Iron Age"],
  ["early_medieval", "Early Medieval"],
  ["viking", "Viking"],
  ["norman", "Norman"],
  ["late_medieval", "Late Medieval"],
  ["plantation", "Plantation"],
  ["early_modern", "Early Modern"],
  ["18th_century", "18th c."],
  ["19th_century", "19th c."],
  ["great_famine", "Famine"],
  ["20th_century", "20th c."],
  ["war_of_independence", "Independence"],
  ["civil_war", "Civil War"],
  ["troubles", "Troubles"],
];

export const PERIOD_COLORS: Record<string, string> = {
  neolithic: "#6e4a2b",
  bronze_age: "#c97b3c",
  iron_age: "#8e3a3a",
  early_medieval: "#c89f3c",
  viking: "#2c5f8d",
  norman: "#4a5568",
  late_medieval: "#6b4e8a",
  plantation: "#4a7c5e",
  early_modern: "#3c8a8e",
  "18th_century": "#b56b8e",
  "19th_century": "#a04545",
  "20th_century": "#5a6878",
  great_famine: "#5e2020",
  war_of_independence: "#2e4a2e",
  civil_war: "#3a3a2e",
  troubles: "#3d2e3d",
  default: "#6b6256",
};

export function periodLabel(p: string): string {
  return PERIODS.find((x) => x[0] === p)?.[1] ?? p;
}

export const ACCESS_LABELS: Record<string, string> = {
  visitor_center: "Visitor centre",
  easy_walk: "Easy walk",
  moderate_walk: "Moderate walk",
  hard_walk: "Hard walk",
  remote: "Remote / boat",
};

export const FAC_LABELS: Record<string, string> = {
  full: "Full facilities",
  basic: "Basic",
  minimal: "Minimal",
  none: "None",
};

/** Coarse groupings of the ~40 site types, for a "what kind of place" facet. */
export const TYPE_GROUPS: { id: string; label: string; types: string[] }[] = [
  { id: "tombs", label: "Tombs & stones", types: ["passage_tomb", "court_cairn", "portal_tomb", "wedge_tomb", "stone_circle", "standing_stone", "ogham_stone", "cursus", "henge", "cairn", "rock_art"] },
  { id: "forts", label: "Forts & royal sites", types: ["hillfort", "ringfort", "promontory_fort", "royal_site", "crannog", "cashel", "stone_fort"] },
  { id: "monastic", label: "Monastic & churches", types: ["monastic_site", "round_tower", "high_cross", "early_church", "abbey", "priory", "friary", "cathedral", "holy_well", "church"] },
  { id: "castles", label: "Castles & towers", types: ["castle", "tower_house", "bawn", "star_fort", "martello_tower", "signal_tower", "fortification"] },
  { id: "houses", label: "Houses & gardens", types: ["country_house", "plantation_town", "garden", "demesne"] },
  { id: "famine", label: "Famine", types: ["famine_workhouse", "famine_mass_grave", "famine_memorial", "workhouse"] },
  { id: "conflict", label: "Battles & revolution", types: ["battlefield", "memorial", "ambush_site", "prison", "gaol", "barracks"] },
  { id: "industrial", label: "Industry & transport", types: ["industrial", "transport_heritage", "lighthouse", "mill", "distillery", "canal"] },
  { id: "museum", label: "Museums", types: ["museum", "visitor_centre", "heritage_centre"] },
];

// ---- Other layers' "kind" labels ----
export const KIND_LABELS: Record<string, string> = {
  // nature
  viewpoint: "Viewpoint", geology: "Geology", ecology: "Wildlife", walk: "Walk", sea_swim: "Sea swim", dark_sky: "Dark sky",
  waterfall: "Waterfall", cave: "Cave", island: "Island", bog: "Bog", woodland: "Woodland", coast: "Coast", mountain_pass: "Mountain pass",
  beach: "Beach", lake: "Lake", geopark: "Geopark", national_park: "National park", nature_reserve: "Nature reserve",
  // gaa
  county_ground: "County ground", club_ground: "Club ground", heritage: "GAA heritage", museum: "Museum", handball_alley: "Handball alley",
  road_bowling: "Road bowling", poc_fada: "Poc Fada", statue: "Statue",
  // food
  cafe: "Café", chipper: "Chipper", pub: "Pub", restaurant: "Restaurant", market: "Market", bakery: "Bakery", smokehouse: "Smokehouse",
  cheesemonger: "Cheesemonger", ice_cream: "Ice cream", takeaway: "Takeaway", farm_shop: "Farm shop", producer: "Producer", butcher: "Butcher",
  seafood_shack: "Seafood shack", tea_rooms: "Tea rooms", deli: "Deli", hotel: "Hotel",
  // equine
  racecourse: "Racecourse", point_to_point: "Point-to-point", eventing: "Eventing", stud: "Stud farm",
  horse_fair: "Horse fair", racing_heritage: "Racing heritage",
  // trains
  station: "Station", heritage_railway: "Heritage railway",
};

export function kindLabel(k: string): string {
  return KIND_LABELS[k] ?? k.replace(/_/g, " ");
}

export const DISH_LABELS: Record<string, string> = {
  full_irish: "Full Irish", ulster_fry: "Ulster fry", spice_bag: "Spice bag", chowder: "Chowder", brown_bread: "Brown bread", soda_bread: "Soda bread",
  soda_farl: "Soda farl", potato_bread: "Potato bread", boxty: "Boxty", coddle: "Coddle", crubeens: "Crubeens", drisheen: "Drisheen", tripe: "Tripe",
  blaa: "Blaa", pastie: "Pastie", belfast_bap: "Belfast bap", oysters: "Oysters", crab: "Crab", prawns: "Prawns", mussels: "Mussels",
  fish_and_chips: "Fish & chips", smoked_salmon: "Smoked salmon", smoked_fish: "Smoked fish", farmhouse_cheese: "Farmhouse cheese",
  black_pudding: "Black pudding", white_pudding: "White pudding", apple_tart: "Apple tart", scones: "Scones", barmbrack: "Barmbrack", tea: "Tea",
  ice_cream_99: "99 ice cream", guinness_stew: "Guinness stew", bacon_and_cabbage: "Bacon & cabbage", lamb: "Lamb", beef: "Beef", seaweed: "Seaweed",
  dulse: "Dulse", eel: "Eel", yellowman: "Yellowman", bramley_apple: "Bramley apple", honey: "Honey", butter: "Butter", spiced_beef: "Spiced beef",
  corned_beef: "Corned beef", crab_claws: "Crab claws", periwinkles: "Periwinkles", carrageen: "Carrageen", porter_cake: "Porter cake",
  breakfast_roll: "Breakfast roll", porridge: "Porridge", vegetable_roll: "Vegetable roll", sausages: "Sausages", rashers: "Rashers",
};

export function dishLabel(d: string): string {
  return DISH_LABELS[d] ?? d.replace(/_/g, " ");
}

export const MEAL_ORDER = ["breakfast", "lunch", "dinner", "snack", "late"];
export const MEAL_LABELS: Record<string, string> = { breakfast: "Breakfast", lunch: "Lunch", dinner: "Dinner", snack: "Snack", late: "Late night" };
export function mealLabel(m: string): string {
  return MEAL_LABELS[m] ?? m.replace(/_/g, " ");
}

/**
 * Extra words a dish should answer to in search ("breakfast" must find a full Irish,
 * "fry" an Ulster fry, "chipper" fish and chips). Space-separated, lower-case.
 */
export const DISH_SEARCH_WORDS: Record<string, string> = {
  full_irish: "breakfast fry fryup fry-up full irish breakfast",
  ulster_fry: "breakfast fry fryup fry-up",
  breakfast_roll: "breakfast roll deli",
  porridge: "breakfast oats oatmeal",
  vegetable_roll: "breakfast fry",
  sausages: "breakfast fry",
  rashers: "breakfast bacon fry",
  black_pudding: "breakfast fry pudding",
  white_pudding: "breakfast fry pudding",
  soda_farl: "breakfast farl bread",
  potato_bread: "breakfast fadge farl",
  boxty: "breakfast potato pancake",
  scones: "breakfast",
  fish_and_chips: "chipper chips",
  ice_cream_99: "ice cream ninety-nine",
  smoked_fish: "kippers haddock",
};

// ---- Equine events (components/Events.tsx) ----
export const EQUINE_KIND_LABELS: Record<string, string> = {
  race_meeting: "Race meeting",
  point_to_point: "Point-to-point",
  horse_trials: "Horse trials",
  show: "Show",
};
export const DISCIPLINE_LABELS: Record<string, string> = {
  flat: "Flat",
  national_hunt: "Jumps",
  mixed: "Flat or jumps",
  point_to_point: "Point-to-point",
  eventing: "Eventing",
  show_jumping: "Show jumping",
  dressage: "Dressage",
  harness: "Harness",
  pony_racing: "Pony racing",
};
export function disciplineLabel(d: string): string {
  return DISCIPLINE_LABELS[d] ?? d.replace(/_/g, " ");
}
export function equineKindLabel(k: string): string {
  return EQUINE_KIND_LABELS[k] ?? k.replace(/_/g, " ");
}
